/**
 * Web4 adapter for decimal config and formatting.
 * Pure logic lives in @Sage/ssm-utils; this layer reads from Context/Redux and calls the package.
 */
import Context from 'managers/Context';
import UtilFormat from 'utils/UtilFormat';
import { getStringAsCurrency } from 'utils/currency';
import {
    CURRENCY_TYPES,
    DECIMAL_TYPES,
    formatDecimal,
    formatLargeNumber,
    getDecimalsByConfig as getResolvedDecimalsByConfig,
    normalizeValue,
    resolveConfigDecimals,
    roundDecimal,
} from '@Sage/ssm-utils';
import { getSelectedMultiEnvironmentIds } from 'utils/multiEnvironment';

export { DECIMAL_TYPES, roundDecimal };
export const normalizeDecimalValue = normalizeValue;

const TEMPORARY_FIELD_MAX_DECIMALS_MAP = {
    SALESORDERSLINES: {
        price: 4,
    },
};

const getEnvironmentConfig = (idEnvironment) => {
    if (!idEnvironment) return null;
    const state = Context.store?.getState();
    const sucursales = state?.serverList?.tblSucursales?.data;
    if (!sucursales || !Array.isArray(sucursales)) return null;
    return sucursales.find((s) => String(s.value) === String(idEnvironment)) || null;
};

const getCurrencyIsoDecimals = (idCurrency) => {
    if (!idCurrency) return null;
    const state = Context.store?.getState();
    const currencies = state?.serverList?.tblCurrency?.data;
    if (!currencies || !Array.isArray(currencies)) return null;
    const currency = currencies.find((c) => String(c.value) === String(idCurrency));
    if (!currency || currency.intdecimalsiso === undefined) return null;
    const decimals = parseInt(currency.intdecimalsiso, 10);
    return isNaN(decimals) ? null : decimals;
};

const getCurrencySymbol = (idCurrency) => {
    const state = Context.store?.getState();
    const userCurrencySymbol = state?.config?.userData?.currencySymbol;
    if (!idCurrency || idCurrency === '-1') return userCurrencySymbol || null;
    const currencies = state?.serverList?.tblCurrency?.data;
    if (!currencies || !Array.isArray(currencies)) return userCurrencySymbol || null;
    const currency = currencies.find((c) => String(c.value) === String(idCurrency));
    return currency?.strsymbol || userCurrencySymbol || null;
};

const getFieldMaxDecimals = (entityName, fieldId) => {
    if (!entityName || !fieldId) return 2;

    const temporaryFieldMaxDecimals =
        TEMPORARY_FIELD_MAX_DECIMALS_MAP?.[String(entityName).toUpperCase()]?.[
            String(fieldId).toLowerCase()
        ];

    if (temporaryFieldMaxDecimals !== undefined) {
        return temporaryFieldMaxDecimals;
    }

    const state = Context.store?.getState();
    const standardMap = state?.config?.standardFieldsSchemaMap?.[entityName];
    const extraMap = state?.config?.extraFieldsSchemaMap?.[entityName];
    if (!standardMap && !extraMap) return null;

    const fieldKeys = [fieldId, typeof fieldId === 'string' ? fieldId.toLowerCase() : null].filter(
        Boolean,
    );

    for (const fKey of fieldKeys) {
        const standardSchema = standardMap?.[fKey];
        if (standardSchema?.maxDecimals !== undefined) {
            const decimals = parseInt(standardSchema.maxDecimals, 10);
            if (!isNaN(decimals)) return decimals;
        }

        const extraSchema = extraMap?.[fKey];
        if (extraSchema?.maxDecimals !== undefined) {
            const decimals = parseInt(extraSchema.maxDecimals, 10);
            if (!isNaN(decimals)) return decimals;
        }
    }

    return 2;
};

const resolveEnvConfigsAndCurrency = (idEnvironment, idCurrency) => {
    let envConfig = null;
    let envConfigs = null;
    let currencyIsoDecimals = idCurrency ? getCurrencyIsoDecimals(idCurrency) : null;

    let effectiveIdEnvironment = idEnvironment;

    if (!effectiveIdEnvironment) {
        const state = Context.store?.getState();
        if (state) {
            const selectedEnvironments = getSelectedMultiEnvironmentIds(state);
            if (selectedEnvironments && selectedEnvironments.length > 1) {
                envConfigs = selectedEnvironments
                    .map((id) => getEnvironmentConfig(id))
                    .filter(Boolean);
                return { envConfig: null, envConfigs, currencyIsoDecimals };
            }
            if (selectedEnvironments && selectedEnvironments.length === 1) {
                effectiveIdEnvironment = selectedEnvironments[0];
            }
        }
        if (!effectiveIdEnvironment) {
            effectiveIdEnvironment = Context?.config?.userData?.idEnvironment;
        }
    }

    if (Array.isArray(effectiveIdEnvironment) && effectiveIdEnvironment.length > 0) {
        envConfigs = effectiveIdEnvironment.map((id) => getEnvironmentConfig(id)).filter(Boolean);
        return { envConfig: null, envConfigs, currencyIsoDecimals };
    }

    envConfig = getEnvironmentConfig(effectiveIdEnvironment);
    return { envConfig, envConfigs: null, currencyIsoDecimals };
};

export const formatDecimalByConfig = ({
    value,
    type,
    idEnvironment,
    idCurrency,
    entityName,
    fieldId,
    locale,
    currencySymbol,
}) => {
    const { envConfig, envConfigs, currencyIsoDecimals } = resolveEnvConfigsAndCurrency(
        idEnvironment,
        idCurrency,
    );

    const effectiveType =
        type && Object.values(DECIMAL_TYPES).includes(type) ? type : DECIMAL_TYPES.GENERAL;
    if (!type || !Object.values(DECIMAL_TYPES).includes(type)) {
        console.warn(`formatDecimalByConfig: Invalid type "${type}". Using GENERAL.`);
    }

    const configDecimals = resolveConfigDecimals({
        type: effectiveType,
        envConfig,
        envConfigs,
        currencyIsoDecimals,
    });

    const fieldMaxDecimals = getFieldMaxDecimals(entityName, fieldId);
    const finalDecimals =
        fieldMaxDecimals != null && fieldMaxDecimals >= 0
            ? Math.min(fieldMaxDecimals, configDecimals)
            : configDecimals;

    const state = Context.store?.getState();
    const userLocale = state?.config?.userData?.locale;
    const effectiveLocale = locale || userLocale || UtilFormat._localeDecimalFormat || 'es-ES';

    const isCurrencyType = CURRENCY_TYPES.includes(effectiveType);
    const effectiveCurrencySymbol = isCurrencyType
        ? currencySymbol || getCurrencySymbol(idCurrency)
        : null;

    return formatDecimal(value, finalDecimals, effectiveLocale, effectiveCurrencySymbol);
};

export const getDecimalsByConfig = ({ type, idEnvironment, idCurrency, entityName, fieldId }) => {
    const effectiveType =
        type && Object.values(DECIMAL_TYPES).includes(type) ? type : DECIMAL_TYPES.GENERAL;
    const { envConfig, envConfigs, currencyIsoDecimals } = resolveEnvConfigsAndCurrency(
        idEnvironment,
        idCurrency,
    );
    const fieldMaxDecimals = getFieldMaxDecimals(entityName, fieldId);

    return getResolvedDecimalsByConfig({
        type: effectiveType,
        envConfig,
        envConfigs,
        currencyIsoDecimals,
        fieldMaxDecimals,
    });
};

/**
 * Round a decimal value for form/input usage using the resolved config decimals.
 * Returns a normalized string with "." decimal separator and no locale formatting.
 */
export const roundDecimalByConfig = ({
    value,
    type,
    idEnvironment,
    idCurrency,
    entityName,
    fieldId,
}) => {
    const numericValue = normalizeValue(value);
    if (numericValue === null) return value != null ? String(value).replace(',', '.') : '';

    const decimals = getDecimalsByConfig({
        type,
        idEnvironment,
        idCurrency,
        entityName,
        fieldId,
    });

    if (!Number.isInteger(decimals) || decimals < 0) return String(numericValue);

    return Number(Math.round(Number(numericValue) + `e${decimals}`) + `e-${decimals}`).toFixed(
        decimals,
    );
};

/**
 * Compact format (K/M/B) while preserving decimal config resolution.
 */
export const formatCompactDecimalByConfig = ({
    value,
    type,
    idEnvironment,
    idCurrency,
    entityName,
    fieldId,
    locale,
    currencySymbol,
}) => {
    const numericValue = normalizeValue(value);
    if (numericValue === null) return value != null ? String(value) : '';

    const effectiveType =
        type && Object.values(DECIMAL_TYPES).includes(type) ? type : DECIMAL_TYPES.GENERAL;
    const decimals = getDecimalsByConfig({
        type: effectiveType,
        idEnvironment,
        idCurrency,
        entityName,
        fieldId,
    });

    const state = Context.store?.getState();
    const userLocale = state?.config?.userData?.locale;
    const effectiveLocale = locale || userLocale || UtilFormat._localeDecimalFormat || 'es-ES';
    const compactValue = formatLargeNumber({
        number: numericValue,
        decimals,
        locale: effectiveLocale,
    });

    if (!CURRENCY_TYPES.includes(effectiveType)) return compactValue;

    const effectiveCurrencySymbol = currencySymbol || getCurrencySymbol(idCurrency);
    return getStringAsCurrency(compactValue, effectiveCurrencySymbol);
};
